/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { isUndefined } from 'lodash';

import { css, StyleSheet } from 'aphrodite';
import { BrandColors, Header, Text } from '@blend/enterprise-components';
import { inputFormatters } from '@blend/shared-react';

import { FormShape } from '../../types';
import { SECTION_AGGREGATION_CONFIGS, CUSTOM_AGGREGATION_CONFIGS } from '../../constants';
import { aggregateFeesBySubsection } from '../../utils/closingFeesUtils';
import { BlendBridge } from 'blb-types/blendBridge';
import { addOnScrollListener, removeOnScrollListener } from '../../utils/eventListenerUtils';
import { useEffect } from 'react';

const styles = StyleSheet.create({
  closingFeesSummary: {
    backgroundColor: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_70}`,
    borderRadius: 3,
    minWidth: 340,
    padding: 32,
  },
  closingFeesSummaryHeader: {
    paddingBottom: 8,
  },
  closingFeesSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
  },
  closingFeesSummarySubRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0 0 16px',
  },
  enablePointerEvents: {
    pointerEvents: 'all',
  },
  loanTermsWrapper: {
    paddingBottom: 36,
  },
});

export namespace ClosingFeesSummary {
  export interface Props {
    formValues: FormShape;
    loan: BlendBridge.API.Loan;
  }
}

interface SummaryRowConfig {
  fontWeight?: Text.Props['weight'];
  text: string;
}

export const ClosingFeesSummary: React.FC<ClosingFeesSummary.Props> = ({ formValues, loan }) => {
  const LOAN_AMOUNT = loan.loanAmount;
  const LOAN_RATE = loan.interestRate;
  const bodyContainerEl = document.querySelectorAll('[class^=bodyContainer]')[0];

  const onScrollFn = () => {
    const containerRightInnerEl = document.getElementById('it-ContainerRightInner');
    if (containerRightInnerEl && bodyContainerEl) {
      containerRightInnerEl.style.transform = `translateX(-${bodyContainerEl.scrollLeft}px)`;
    }
  };

  useEffect(() => {
    addOnScrollListener(bodyContainerEl, onScrollFn);
    return () => {
      removeOnScrollListener(bodyContainerEl, onScrollFn);
    };
  }, []);

  const renderSummarySubRow = (label: string, value: string, id: string) => {
    return (
      <div className={css(styles.closingFeesSummarySubRow)}>
        <span className={css(styles.enablePointerEvents)}>
          <Text data-it={`${id}-Label`} displayBlock fontSize="md">
            {label}
          </Text>
        </span>
        <span className={css(styles.enablePointerEvents)}>
          <Text data-it={`${id}-Value`} fontSize="md" weight="medium">
            {value}
          </Text>
        </span>
      </div>
    );
  };

  const renderSummaryRow = (label: SummaryRowConfig, value: SummaryRowConfig, id: string) => {
    const { text: labelText, fontWeight: labelFontWeight } = label;
    const { text: valueText, fontWeight: valueFontWeight } = value;
    return (
      <div className={css(styles.closingFeesSummaryRow)}>
        <span className={css(styles.enablePointerEvents)}>
          <Text data-it={`${id}-Label`} fontSize="md" weight={labelFontWeight}>
            {labelText}
          </Text>
        </span>
        <span className={css(styles.enablePointerEvents)}>
          <Text data-it={`${id}-Value`} fontSize="md" weight={valueFontWeight}>
            {valueText}
          </Text>
        </span>
      </div>
    );
  };

  const renderLoanTermsSection = () => {
    const interestRate = LOAN_RATE;
    const loanAmount = LOAN_AMOUNT;

    return (
      <div className={css(styles.loanTermsWrapper)}>
        <div className={css(styles.closingFeesSummaryHeader)}>
          <Header fontWeight="BOLD" size={4}>
            Loan Terms
          </Header>
        </div>
        {renderSummaryRow(
          { text: 'Loan Amount' },
          { text: loanAmount ? inputFormatters.MONEY_CENTS.formatWithNegativeParens(loanAmount.toFixed(2)) : '-' },
          'LoanAmount',
        )}
        {renderSummaryRow(
          { text: 'Interest Rate' },
          { text: interestRate ? inputFormatters.PERCENTAGE.format(interestRate) : '-' },
          'InterestRate',
        )}
      </div>
    );
  };

  const renderCostsAtClosingSection = () => {
    const loanAmount = LOAN_AMOUNT;
    const lenderCredits = aggregateFeesBySubsection(formValues, SECTION_AGGREGATION_CONFIGS.LenderCredits);
    const closingCosts = aggregateFeesBySubsection(formValues, CUSTOM_AGGREGATION_CONFIGS.ClosingCosts);
    const payoff = aggregateFeesBySubsection(formValues, SECTION_AGGREGATION_CONFIGS.PayoffsAndPayments);
    const closingCostsBeforeClosing = aggregateFeesBySubsection(
      formValues,
      CUSTOM_AGGREGATION_CONFIGS.ClosingCosts,
      fee => fee.paymentMilestone === 'BeforeClosing',
    );
    const totalClosingCosts = lenderCredits + closingCosts;
    const cashToClose = !isUndefined(loanAmount)
      ? loanAmount - totalClosingCosts + closingCostsBeforeClosing - payoff
      : null;

    return (
      <div>
        <div className={css(styles.closingFeesSummaryHeader)}>
          <Header fontWeight="BOLD" size={4}>
            Costs at Closing
          </Header>
        </div>
        {renderSummaryRow(
          { text: 'Total Closing Costs' },
          { text: inputFormatters.MONEY_CENTS.formatWithDollarSignAndCents(totalClosingCosts) },
          'TotalClosingCosts',
        )}
        {renderSummarySubRow(
          'Closing Costs',
          inputFormatters.MONEY_CENTS.formatWithDollarSignAndCents(closingCosts),
          'ClosingCosts',
        )}
        {renderSummarySubRow(
          'Lender Credits',
          inputFormatters.MONEY_CENTS.formatWithNegativeParens(lenderCredits.toFixed(2)),
          'LenderCredits',
        )}
        {renderSummaryRow(
          { text: 'Cash to Close' },
          {
            text: cashToClose ? inputFormatters.MONEY_CENTS.formatWithDollarSignAndCents(cashToClose) : '-',
          },
          'CashToClose',
        )}
      </div>
    );
  };

  return (
    <div className={css(styles.closingFeesSummary)}>
      {renderLoanTermsSection()}
      {renderCostsAtClosingSection()}
    </div>
  );
};
